import React, { useState, useEffect, useRef } from "react";
import classNames from "classnames";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import UserDefaultAvatar from "assets/img/user.svg";
import MediaPreview from "components/MediaPreview/MediaPreview";
import PostDetailModal from "components/PostDetailModal";
import { useGlobalContext } from "store/contexts/globalContext";
import { useTimelineContext } from "store/contexts/timelineContext";

import {
  faAngleLeft,
  faAngleRight,
  faPlay,
  faPause,
} from "@fortawesome/free-solid-svg-icons";

const styles = {
  avatarImg: {
    borderRadius: "50%",
    color: "transparent",
    width: "100%",
    height: "100%",
    objectFit: "cover",
    textAlign: "center",
    textIndent: "10000px",
  },
  icon: {
    width: "25px",
    height: "25px",
    marginRight: "5px",
  },
  overlay: {
    width: "100%",
    height: "430px",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    background: "linear-gradient(60deg, #1176b8, #fccc41)",
  },
  container: {
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
  },
  playerBar: {
    userSelect: "none",
    bottom: "0px",
    left: "0",
    height: "50px",
    width: "100%",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    background: "#a3a3a3",
    wordBreak: "break-all",
  },
  playerControls: {
    display: "flex",
    alignItems: "center",
  },
  userName: {
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
    maxWidth: "100px",
  },
  userInfo: {
    paddingRight: "5px",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    color: "white",
    fontWeight: "400",
  },
  avatar: {
    marginRight: "5px",
    height: "30px",
    width: "30px",
  },
  playerContent: {
    color: "white",
    fontWeight: "500",
    fontSize: "initial",
    left: 0,
    width: "100%",
    bottom: "55px",
    height: "auto",
    display: "flex",
    position: "absolute",
    background: "#0000005c",
    maxHeight: "100px",
    overflow: "auto",
  },
  mainImage: {
    display: "flex",
    justifyContent: "center",
    backgroundColor: "black",
    height: "430px",
    alignItems: "center",
  },
};

export default ({ imageResponses, postPublished, post }) => {
  const playerRef = useRef();
  const contentRef = useRef();
  const classes = styles;
  const [played, setPlayed] = useState(null);
  const [timer, setTimer] = useState();
  const [loadImage, setLoadImage] = useState(false);
  const [currentImage, setCurrentImage] = useState(0);
  const { ringCentral } = useGlobalContext();
  const { selectedPost } = useTimelineContext();

  const onImageEnd = () => {
    if (currentImage + 1 < imageResponses.length) {
      setCurrentImage(currentImage + 1);
    } else {
      setPlayed(false);
    }
  };

  const playImage = async () => {
    setPlayed(true);

    setTimer(
      setTimeout(() => {
        nextImage();
      }, 3000)
    );
  };

  const pauseImage = () => {
    setPlayed(false);
    clearTimeout(timer);
  };

  const nextImage = () => {
    if (currentImage === imageResponses.length - 1) {
      setPlayed(false);
      setCurrentImage(0);
      setLoadImage(false);
      return;
    }
    setCurrentImage(currentImage + 1);
    setLoadImage(false);
  };

  const previousImage = () => {
    if (currentImage === 0) {
      return;
    }

    setCurrentImage(currentImage - 1);
    setLoadImage(false);
  };

  useEffect(() => {
    if (currentImage === null || !imageResponses[currentImage]?.media) {
      return;
    }

    playerRef.current.src = imageResponses[currentImage].media;

    if (loadImage) {
      contentRef.current.textContent =
        imageResponses[currentImage].content !== "undefined"
          ? imageResponses[currentImage].content
          : "";
    }

    if (played && loadImage) {
      playImage();
    }

    return () => clearTimeout(timer);

    // eslint-disable-next-line
  }, [currentImage, loadImage]);

  if (!imageResponses.length) {
    return <div>No image responses found.</div>;
  }

  const checkImageLoad = (e) => {
    setLoadImage(true);
  };

  return (
    <div style={{ width: "100%" }}>
      <div style={classes.container}>
        <div style={classes.mainImage}>
          <MediaPreview
            mediaClassName="slideShowImage"
            attachment={imageResponses[currentImage]?.media}
            type={"image"}
          >
            <img
              ref={playerRef}
              onLoad={checkImageLoad}
              alt={`${imageResponses.media}`}
              className="imagePlayerImage"
              playsInline
              preload="metadata"
              onEnded={onImageEnd}
            />
          </MediaPreview>
        </div>
        {!postPublished && !ringCentral && !selectedPost && (
          <PostDetailModal post={post} />
        )}
        <div
          ref={contentRef}
          style={classes.playerContent}
          className="customScrollArea"
        >
          <span />
        </div>
        {postPublished && (
          <div style={classes.playerBar}>
            <div style={classes.playerControls}>
              <FontAwesomeIcon
                style={classes.icon}
                className={classNames({
                  cursorIcon: (currentImage !== 0 || !played) && true,
                  disableCursorIcon: (currentImage === 0 || played) && true,
                  "material-icons": true,
                })}
                icon={faAngleLeft}
                onClick={previousImage}
              />
              {!played && (
                <FontAwesomeIcon
                  className="material-icons"
                  style={{
                    width: "13px",
                    cursor: "pointer",
                    color: "#ff9800",
                  }}
                  icon={faPlay}
                  onClick={playImage}
                />
              )}
              {played && (
                <FontAwesomeIcon
                  className="material-icons"
                  style={{
                    width: "13px",
                    cursor: "pointer",
                    color: "#ff9800",
                  }}
                  onClick={pauseImage}
                  icon={faPause}
                />
              )}
              <FontAwesomeIcon
                style={classes.icon}
                className={classNames({
                  cursorIcon:
                    (currentImage !== imageResponses.length - 1 || !played) &&
                    true,
                  disableCursorIcon:
                    (currentImage === imageResponses.length - 1 || played) &&
                    true,
                  "material-icons": true,
                })}
                onClick={nextImage}
                icon={faAngleRight}
              />
            </div>
            <div style={classes.userInfo}>
              <div style={classes.avatar}>
                <img
                  alt={`${imageResponses[currentImage]?.user?.full_name}`}
                  style={classes.avatarImg}
                  aria-label={imageResponses[currentImage]?.user?.full_name}
                  src={
                    imageResponses[currentImage]?.user?.avatar ??
                    UserDefaultAvatar
                  }
                />
              </div>
              <span style={classes.userName}>
                {imageResponses[currentImage]?.user?.full_name}
              </span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
